import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';

import type { FollowUpSource } from '../constants';
import type { AIMateAnalyticsSource } from '../types';

import type {
	AIMateAttributeType,
	AnalyticsPropertiesContextProps,
	ConversationAssistantUIModes,
	SharedFeaturesInstrumentationIdContextProps,
} from './types';

const initialState: AIMateAttributeType = {
	conversationChannelId: '',
	chatEntryPoint: undefined,
	experienceId: '',
	product: '',
	productHeader: '',
	inProductExperience: '',
	agentId: null,
	agentExternalConfigReference: null,
	agentActions: [],
	agentIsDefault: null,
	agentIsVerified: null,
	fullScreen: false,
	agentCreatorType: '',
	agentVisibility: '',
	singleInstrumentationID: '',
	mode: undefined,
};

const AnalyticsPropertiesContext = createContext<AnalyticsPropertiesContextProps>({
	propertiesRef: {
		current: { ...initialState },
	},
	mode: undefined,
	setAnalyticsPropertiesWithoutRerender: () => {},
});
export const SharedFeaturesInstrumentationIdContext: React.Context<SharedFeaturesInstrumentationIdContextProps> =
	createContext<SharedFeaturesInstrumentationIdContextProps>({
		sharedFeaturesInstrumentationId: undefined,
		isInSmartCreateFlow: undefined,
	});

/**
 * This context contains ref to the properties that will be added to `useAnalytics` when firing an event
 * Using `ref` so this context does not rerender when any of the analytics properties change
 * Because the updated attributes are only needed when the analytics is fired
 */
export const AnalyticsPropertiesContextProvider = ({
	children,
	mode,
	launchSource,
}: {
	children: ReactNode;
	mode: ConversationAssistantUIModes;
	launchSource?: FollowUpSource;
}): React.JSX.Element => {
	const propertiesRef = useRef<AIMateAttributeType>({
		...initialState,
		launchSource,
	});

	// Sync the (possibly late/changing) prop into the ref after commit, so concurrent
	// renders that are paused or discarded never leak an uncommitted launch source.
	useEffect(() => {
		propertiesRef.current.launchSource = launchSource;
	}, [launchSource]);

	const setAnalyticsPropertiesWithoutRerender = useCallback(
		(properties: Partial<AIMateAttributeType>) => {
			propertiesRef.current = { ...propertiesRef.current, ...properties };
		},
		[],
	);

	const value = useMemo(
		() => ({
			propertiesRef,
			mode,
			setAnalyticsPropertiesWithoutRerender,
		}),
		[setAnalyticsPropertiesWithoutRerender, mode],
	);

	return (
		<AnalyticsPropertiesContext.Provider value={value}>
			{children}
		</AnalyticsPropertiesContext.Provider>
	);
};

/**
 * Adding extra wrapper to AnalyticsPropertiesContextProvider
 * for `source` attribute
 * using AnalyticsContext
 *
 * WARNING: This provider must not appear more than once in the component tree.
 */
export const AnalyticsContextWithSourceAndProperties = ({
	source,
	sharedFeaturesInstrumentationId,
	iframeHostProduct,
	children,
	mode,
	isAlwaysInSmartCreateFlow,
	launchSource,
}: {
	source: AIMateAnalyticsSource;
	sharedFeaturesInstrumentationId?: string;
	iframeHostProduct?: string;
	children: ReactNode;
	mode?: ConversationAssistantUIModes;
	isAlwaysInSmartCreateFlow?: boolean;
	launchSource?: FollowUpSource;
}): React.JSX.Element => {
	const {
		sharedFeaturesInstrumentationId: parentSharedFeaturesInstrumentationId,
		isInSmartCreateFlow: parentIsInSmartCreateFlow,
	} = useContext(SharedFeaturesInstrumentationIdContext);

	const isInSmartCreateFlow =
		isAlwaysInSmartCreateFlow === undefined ? parentIsInSmartCreateFlow : isAlwaysInSmartCreateFlow;

	const data = useMemo(
		() => ({
			source,
			attributes: {
				sharedFeaturesInstrumentationId:
					sharedFeaturesInstrumentationId ?? parentSharedFeaturesInstrumentationId,
				isInSmartCreateFlow,
				iframeHostProduct,
			},
		}),
		[
			source,
			sharedFeaturesInstrumentationId,
			parentSharedFeaturesInstrumentationId,
			isInSmartCreateFlow,
			iframeHostProduct,
		],
	);

	return (
		<AnalyticsContext data={data}>
			<SharedFeaturesInstrumentationIdContext.Provider
				value={{
					sharedFeaturesInstrumentationId:
						sharedFeaturesInstrumentationId ?? parentSharedFeaturesInstrumentationId,
					isInSmartCreateFlow,
				}}
			>
				<AnalyticsPropertiesContextProvider mode={mode} launchSource={launchSource}>
					{children}
				</AnalyticsPropertiesContextProvider>
			</SharedFeaturesInstrumentationIdContext.Provider>
		</AnalyticsContext>
	);
};

export const useAnalyticsPropertiesContext = (): AnalyticsPropertiesContextProps => {
	return useContext(AnalyticsPropertiesContext);
};
